'use client';

import type { ReactNode, JSX } from 'react';
import type React from 'react';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import { HEADER_HEIGHT_CSS } from 'stores/zustand/layoutStore';
import { useOverlayCoverage } from 'stores/zustand/overlayCoverageStore';
import { useScrollLockStore } from 'stores/zustand/scrollLockStore';
import { dynamicWithRetry } from 'utils/dynamicWithRetry';

import type { CorePopoverProps } from './types';

const Popover = dynamicWithRetry(() => import('@mui/material/Popover'));

/**
 * How long the popover takes to animate away. The scroll lock and the page-coverage signal
 * are both held for this long after `show` goes false, so neither hands the page back while
 * the dismissal is still rendering.
 */
const CLOSE_TRANSITION_MS = 100;
interface PopoverProps extends CorePopoverProps {
    children: ReactNode;
    forceRightAlign?: boolean;
    forceLeftAlign?: boolean;
    onClose?: () => void;
    fullScreen?: boolean;
}

const PopoverComponent = ({
    children,
    content,
    className,
    style,
    popoverHandleRef,
    disableDefaultMinWidth,
    forceRightAlign,
    forceLeftAlign,
    onClose,
    fullScreen,
    ...boxProps
}: PopoverProps): // If ref is passed, the popover must be externally opened
// ref: React.Ref<PopoverHandle>,
JSX.Element => {
    const scrollLocked = useScrollLockStore((s) => s.scrollLocked);
    const { lockScroll, unlockScroll } = useScrollLockStore((s) => s.actions);

    const [show, setShow] = useState(false);
    const unlockScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Only the full-screen form (`NavbarMenu` / notifications on mobile) covers the page; a
    // desktop dropdown leaves it readable, and pausing background work behind one would
    // freeze prices the user can still see.
    //
    // Held for the close transition, not dropped the instant `show` flips: resuming quote
    // subscriptions, polling and autoplay while the popover is still animating away puts that
    // work up against the dismissal on the devices the pause exists for. Driven off `show`
    // rather than off the scroll lock — the lock's own release is deferred the same way, and a
    // handoff (`Menu.onAddFunds` closes this and opens the funding dialog) must not read as
    // "nothing is covering the page" in between.
    const [covering, setCovering] = useState(false);
    useEffect(() => {
        if (show) {
            setCovering(true);
            return;
        }
        if (!covering) return;
        const timer = setTimeout(() => setCovering(false), CLOSE_TRANSITION_MS);
        return () => clearTimeout(timer);
    }, [show, covering]);
    useOverlayCoverage(covering && !!fullScreen);

    const closePopover = (dontUnlockScroll?: boolean): void => {
        setShow(false);
        if (onClose) {
            onClose();
        }

        if (!dontUnlockScroll) {
            if (unlockScrollTimeoutRef.current !== null) {
                clearTimeout(unlockScrollTimeoutRef.current);
            }
            unlockScrollTimeoutRef.current = setTimeout(() => {
                unlockScroll();
                // Clear the ref so the unmount cleanup doesn't later re-run unlockScroll
                // and steal a lock a different overlay has since acquired.
                unlockScrollTimeoutRef.current = null;
            }, CLOSE_TRANSITION_MS); // Delay to allow the popover to close before unlocking scroll
        }
    };

    // Clear any pending unlock timer on unmount so it can't fire (and touch
    // `document`) after the component is gone; unlock synchronously instead so a
    // close-then-unmount doesn't leave the body scroll-locked.
    useEffect(
        () => () => {
            if (unlockScrollTimeoutRef.current !== null) {
                clearTimeout(unlockScrollTimeoutRef.current);
                unlockScroll();
            }
        },
        [unlockScroll],
    );

    // A simple wrapper with `display: contents`
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Element which contains the popover's contents

    // Popover can be externally opened using ref.current.open()
    useImperativeHandle(popoverHandleRef, () => ({
        open: (): void => setShow(true),
        close: (dontUnlockScroll?: boolean): void => closePopover(dontUnlockScroll),
        isOpen: show,
    }));

    // Open popover when child is clicked
    const onWrapperClick = (event: React.MouseEvent<HTMLSpanElement>): void => {
        // Ignore clicks in the wrapper but outside of children.
        // This can happen if children have steep border radii
        if (!popoverHandleRef && !wrapperRef.current?.isEqualNode(event.target as Node)) {
            setShow(true);
        }
    };

    // Don't add unlock here because it will be triggered on every render
    useEffect(() => {
        if (show && !scrollLocked) {
            lockScroll();
        }
    }, [show, scrollLocked, lockScroll]);

    const alignment = forceLeftAlign ? 'left' : forceRightAlign ? 'right' : 'center';

    // Adjust height for fullScreen when smart banner is present
    const fullScreenStyle = useMemo(
        () =>
            fullScreen
                ? {
                      ...style,
                      height: `calc(100dvh - ${HEADER_HEIGHT_CSS})`,
                  }
                : style,
        [fullScreen, style],
    );

    return (
        <>
            <Popover
                open={show}
                onClose={(): void => closePopover()}
                anchorEl={wrapperRef.current}
                anchorOrigin={{
                    horizontal: alignment,
                    vertical: 'bottom',
                }}
                transformOrigin={{
                    horizontal: alignment,
                    vertical: 'top',
                }}
                disableEnforceFocus
                disableAutoFocus
                style={{ ...style }}
                PaperProps={{
                    style: {
                        ...fullScreenStyle,
                        ...(fullScreen ? { top: HEADER_HEIGHT_CSS } : {}),
                    },
                    className: clsx(
                        '!bg-surface-x40 dark:!bg-surface-x10 overflow-y-auto custom-scrollbar',
                        fullScreen
                            ? '!shadow-none fixed w-screen !max-w-none rounded-[0] !left-0 !right-0 overflow-y-auto z-critical'
                            : clsx(
                                  ' !shadow-x20 !rounded-x30 w-max !max-w-[480px] !max-h-[calc(100vh-32px)] mt-1',
                                  !disableDefaultMinWidth && '!min-w-[200px]',
                              ),
                        className,
                    ),
                }}
                {...boxProps}
            >
                {content}
            </Popover>
            <span ref={wrapperRef} onClick={onWrapperClick}>
                {children}
            </span>
        </>
    );
};

export default PopoverComponent;
